import React from 'react';
import '@/styles/PhoneInputCustom.css';
import { getIconClass } from '@/helpers';
import { useFormContext } from '@/providers';
import { PROFILE_IMAGE_SERVER_URL } from '@/constants';
import PhoneNumberInput from './PhoneNumberInput';
import { MdQuestionMark } from 'react-icons/md';
import Swal from 'sweetalert2';


export const ProfileSection = () => {
    const {
        register,
        setValue,
        errors,
        watch,
        fieldRefs
    } = useFormContext();


    const watchFields = {
        air_men: watch('air_men'),
        air_men_back: watch('air_men_back'),
        driving_license: watch('driving_license'),
        driving_license_back: watch('driving_license_back'),
    };


    const renderPreview = (file) => {
        return (
            <div className="h-[250px] flex justify-center items-center overflow-hidden border border-gray-200 rounded-md">
                {file && file[0] ? (
                    <img
                        className="object-cover w-full h-full"
                        src={typeof file !== "string" ? URL.createObjectURL(file[0]) : `${PROFILE_IMAGE_SERVER_URL}/${file}`}
                        alt="Preview"
                    />
                ) : (
                    <div className="text-center text-gray-500">
                        <span>
                            <i className={getIconClass("Document Upload")} /> Click here or drop files to upload
                        </span>
                        <div className="photoUpload-files" />
                    </div>
                )}
            </div>
        );
    };


    return (
        <div className="max-w-4xl p-6 mx-auto bg-white rounded-lg shadow-md">
            {/* <div className="custom-form"> */}
            <div className="mb-6 text-center">
                <h3 className="text-lg font-semibold">Information</h3>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* First Name */}
                <div ref={(el) => (fieldRefs.current["first_name"] = el)}>
                    <label className="block text-sm font-medium">
                        First Name <i className={getIconClass('First Name')} />
                    </label>
                    <input type="text"
                        className="w-full p-2 border rounded-md"
                        {...register('first_name', { onChange: (e) => setValue('first_name', e.target.value, { shouldValidate: true }) })}
                        placeholder="First Name" />
                    {errors.first_name && <span className="inline-block w-full overflow-hidden text-xs text-red-500 truncate whitespace-nowrap">{errors.first_name.message}</span>}
                </div>

                {/* Last Name */}
                <div ref={(el) => (fieldRefs.current["last_name"] = el)}>
                    <label className="block text-sm font-medium">
                        Last Name <i className={getIconClass('Last Name')} />
                    </label>
                    <input type="text"
                        className="w-full p-2 border rounded-md"
                        {...register('last_name', { onChange: (e) => setValue('last_name', e.target.value, { shouldValidate: true }) })}
                        placeholder="Last Name" />
                    {errors.last_name && <span className="inline-block w-full overflow-hidden text-xs text-red-500 truncate whitespace-nowrap">{errors.last_name.message}</span>}
                </div>

                {/* Username */}
                <div ref={(el) => (fieldRefs.current["username"] = el)}>
                    <label className="block text-sm font-medium">Username</label>
                    <input type="text"
                        readOnly
                        className="w-full p-2 border rounded-md"
                        {...register('username', { onChange: (e) => setValue('username', e.target.value, { shouldValidate: true }) })}
                        placeholder="Username" />
                    {errors.username && <span className="inline-block w-full overflow-hidden text-xs text-red-500 truncate whitespace-nowrap">{errors.username.message}</span>}
                </div>

                {/* Display Name */}
                <div ref={(el) => (fieldRefs.current["display_name"] = el)}>
                    <label className="inline-flex items-center !m-0 text-sm font-medium">
                        Display Name
                        <MdQuestionMark
                            onClick={() =>
                                Swal.fire({
                                    text: "Your display name is what people see publicly. For privacy purposes, we cannot allow any part of your username, tail number, first name, last name, initials, business name, or any other information that easily identifies you. If we find such information, we will change it to something suitable on your behalf until you have a chance to change it yourself. Acceptable names examples are PilotsDaughter, StearmanLover, FlyingDutchman, et al.",
                                    icon: "question",
                                })
                            }
                            style={{ color: "#8ec639", cursor: "pointer" }}
                        />
                    </label>
                    <input type="text"
                        className="w-full p-2 border rounded-md"
                        {...register('display_name', { onChange: (e) => setValue('display_name', e.target.value, { shouldValidate: true }) })}
                        placeholder="Display Name" />
                    {errors.display_name && <span className="inline-block w-full overflow-hidden text-xs text-red-500 truncate whitespace-nowrap">{errors.display_name.message}</span>}
                </div>

                {/* Native Language */}
                <div ref={(el) => (fieldRefs.current["native_language"] = el)}>
                    <label className="block text-sm font-medium">Native Language</label>
                    <input type="text"
                        className="w-full p-2 border rounded-md"
                        {...register('native_language', { onChange: (e) => setValue('native_language', e.target.value, { shouldValidate: true }) })}
                        placeholder="Native Language" />
                    {errors.native_language && <span className="inline-block w-full overflow-hidden text-xs text-red-500 truncate whitespace-nowrap">{errors.native_language.message}</span>}
                </div>

                {/* Other Language */}
                <div ref={(el) => (fieldRefs.current["other_language"] = el)}>
                    <label className="block text-sm font-medium">Other Language</label>
                    <input type="text"
                        className="w-full p-2 border rounded-md"
                        {...register('other_language', { onChange: (e) => setValue('other_language', e.target.value) })}
                        placeholder="Other Language" />
                </div>

                {/* Phone Number */}
                <div ref={(el) => (fieldRefs.current["phone"] = el)}>
                    <PhoneNumberInput name="phone" label="Phone Number" defaultCountry="US" />
                </div>

                {/* Other Phone Number */}
                <div ref={(el) => (fieldRefs.current["other_phone"] = el)}>
                    <PhoneNumberInput name="other_phone" label="Other Phone Number" defaultCountry="US" />
                </div>

                {/* Email */}
                <div ref={(el) => (fieldRefs.current["email"] = el)}>
                    <label className="block text-sm font-medium">Email</label>
                    <input className="w-full p-2 border rounded-md" type="email" {...register('email')} placeholder="Email" disabled />
                    {errors.email && <span className="inline-block w-full overflow-hidden text-xs text-red-500 truncate whitespace-nowrap">{errors.email.message}</span>}
                </div>

                {/* Additional Email */}
                <div ref={(el) => (fieldRefs.current["additional_email"] = el)}>
                    <label className="block text-sm font-medium">Additional Email</label>
                    <input className="w-full p-2 border rounded-md" type="email" {...register('additional_email')} placeholder="Additional Email" />
                    {errors.additional_email && <span className="inline-block w-full overflow-hidden text-xs text-red-500 truncate whitespace-nowrap">{errors.additional_email.message}</span>}
                </div>

                <>
                    {/* Airmen Certificate Front */}
                    <div style={{ marginTop: '12px' }} ref={(el) => (fieldRefs.current["air_men"] = el)}>
                        <label className="block text-sm font-medium">Airmen Certificate Front</label>
                        <div className="listsearch-input-item ">
                            <div className="fuzone">
                                {renderPreview(watchFields.air_men)}
                                <input
                                    type="file"
                                    className="upload"
                                    {...register('air_men')}
                                    accept=".jpg,.jpeg,.png"
                                />
                                {errors.air_men && (
                                    <span className="inline-block w-full overflow-hidden text-xs text-red-500 truncate whitespace-nowrap">{errors.air_men.message}</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Airmen Certificate Back */}
                    <div style={{ marginTop: '12px' }} ref={(el) => (fieldRefs.current["air_men_back"] = el)}>
                        <label className="block text-sm font-medium">Airmen Certificate Back</label>
                        <div className="listsearch-input-item ">
                            <div className="fuzone">
                                {renderPreview(watchFields.air_men_back)}
                                <input
                                    type="file"
                                    className="upload"
                                    {...register('air_men_back')}
                                    accept=".jpg,.jpeg,.png"
                                />
                                {errors.air_men_back && (
                                    <span className="inline-block w-full overflow-hidden text-xs text-red-500 truncate whitespace-nowrap">{errors.air_men_back.message}</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Driving License Front */}
                    <div style={{ marginTop: '12px' }} ref={(el) => (fieldRefs.current["driving_license"] = el)}>
                        <label className="block text-sm font-medium">Driving License Front</label>
                        <div className="listsearch-input-item ">
                            <div className="fuzone">
                                {renderPreview(watchFields.driving_license)}
                                <input
                                    type="file"
                                    className="upload"
                                    {...register('driving_license')}
                                    accept=".jpg,.jpeg,.png"
                                />
                                {errors.driving_license && (
                                    <span className="inline-block w-full overflow-hidden text-xs text-red-500 truncate whitespace-nowrap">{errors.driving_license.message}</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Driving License Back */}
                    <div style={{ marginTop: '12px' }} ref={(el) => (fieldRefs.current["driving_license_back"] = el)}>
                        <label className="block text-sm font-medium">Driving License Back</label>
                        <div className="listsearch-input-item ">
                            <div className="fuzone">
                                {renderPreview(watchFields.driving_license_back)}
                                <input
                                    type="file"
                                    className="upload"
                                    {...register('driving_license_back')}
                                    accept=".jpg,.jpeg,.png"
                                />
                                {errors.driving_license_back && (
                                    <span className="inline-block w-full overflow-hidden text-xs text-red-500 truncate whitespace-nowrap">{errors.driving_license_back.message}</span>
                                )}
                            </div>
                        </div>
                    </div>
                </>

                {/* Bio Section */}

            </div>
            <div ref={(el) => (fieldRefs.current["bio"] = el)}>
                <label className="block text-sm font-medium">Bio</label>
                <textarea className="w-full p-2 border rounded-md" {...register('bio')} placeholder="Write your bio here..." />
                {errors.bio && <span className="inline-block w-full overflow-hidden text-xs text-red-500 truncate whitespace-nowrap">{errors.bio.message}</span>}
            </div>
            {/* </div> */}
        </div>
    )
}
